/**
 * Enhanced Facility Selector Component with Autocomplete
 * Provides autocomplete search for verified waste facilities
 */

import { useEffect, useState, useRef } from 'react';
import type { WasteStreamType } from '../../types';

interface Facility {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  type: string;
  current_verification_status?: 'verified' | 'pending' | 'expired' | 'unverified';
  distance_miles?: number;
}

interface FacilitySelectorProps {
  materialType: WasteStreamType;
  city?: string;
  value: string;
  onChange: (facilityName: string, facilityAddress?: string) => void;
}

/**
 * Enhanced facility selector with autocomplete functionality
 * Fetches facilities from API and provides type-ahead search
 */
export function FacilitySelector({
  materialType,
  city,
  value,
  onChange,
}: FacilitySelectorProps) {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter facilities based on input
  const filteredFacilities = facilities.filter((facility) =>
    facility.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  // Fetch facilities when material type or city changes
  useEffect(() => {
    const fetchFacilities = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append('material', materialType);
        if (city) {
          params.append('city', city);
        }

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/compliance/facilities?${params}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch facilities');
        }

        const data = await response.json();
        setFacilities(data.facilities || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load facilities');
      } finally {
        setLoading(false);
      }
    };

    if (materialType) {
      fetchFacilities();
    }
  }, [materialType, city]);

  // Update input when value prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setSelectedIndex(-1);
    // Allow custom input (freeSolo behavior)
    onChange(newValue);
  };

  const handleFacilitySelect = (facility: Facility) => {
    setInputValue(facility.name);
    setIsOpen(false);
    setSelectedIndex(-1);
    const fullAddress = `${facility.address}, ${facility.city}, ${facility.state}`;
    onChange(facility.name, fullAddress);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredFacilities.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredFacilities[selectedIndex]) {
          handleFacilitySelect(filteredFacilities[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleInputFocus = () => {
    if (filteredFacilities.length > 0) {
      setIsOpen(true);
    }
  };

  if (loading) {
    return (
      <div>
        <label htmlFor="facility-selector" className="block text-sm font-medium text-neutral-700 mb-1">
          Facility {city && <span className="text-neutral-500">(in {city})</span>}
        </label>
        <div className="input-field bg-neutral-50 text-neutral-500">
          Loading facilities...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <label htmlFor="facility-selector" className="block text-sm font-medium text-neutral-700 mb-1">
          Facility {city && <span className="text-neutral-500">(in {city})</span>}
        </label>
        <div className="input-field border-red-300 bg-red-50">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <label htmlFor="facility-selector" className="block text-sm font-medium text-neutral-700 mb-1">
        Facility {city && <span className="text-neutral-500">(in {city})</span>}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          id="facility-selector"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="input-field pr-10"
          placeholder="Search facilities or enter custom name..."
          aria-autocomplete="list"
          aria-controls="facility-dropdown"
          aria-expanded={isOpen}
          aria-activedescendant={
            selectedIndex >= 0 ? `facility-option-${selectedIndex}` : undefined
          }
        />

        {/* Search icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className="w-5 h-5 text-neutral-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && filteredFacilities.length > 0 && (
        <div
          ref={dropdownRef}
          id="facility-dropdown"
          role="listbox"
          className="absolute z-10 w-full mt-1 bg-white border border-neutral-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredFacilities.map((facility, index) => (
            <div
              key={facility.id}
              id={`facility-option-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                index === selectedIndex
                  ? 'bg-primary-50 text-primary-900'
                  : 'hover:bg-neutral-50'
              } ${index > 0 ? 'border-t border-neutral-100' : ''}`}
              onClick={() => handleFacilitySelect(facility)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-neutral-900 truncate">
                      {facility.name}
                    </span>
                    {facility.current_verification_status === 'verified' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Verified
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600 truncate">
                    {facility.address}, {facility.city}, {facility.state}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-neutral-500 capitalize">
                      {facility.type}
                    </span>
                    {facility.distance_miles !== undefined && (
                      <span className="text-xs text-neutral-500">
                        {facility.distance_miles.toFixed(1)} mi
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Helper text */}
      <p className="text-sm text-neutral-500 mt-1">
        {facilities.length === 0 ? (
          city ? (
            `No facilities found in ${city} for this material type`
          ) : (
            'No facilities found for this material type'
          )
        ) : (
          `${facilities.length} ${facilities.length === 1 ? 'facility' : 'facilities'} available`
        )}
      </p>
    </div>
  );
}
